import React, { createContext, useContext, useEffect } from "react";
import { ThemeConfig, buttonStyleVars, setThemeVars } from "@servicepro/jobsite-components";

interface ThemeContextValue {
  theme: ThemeConfig;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined),

  useTheme = (): ThemeContextValue => {
    const context = useContext(ThemeContext);
    if (!context) {
      throw new Error("useTheme must be used within ThemeProvider");
    }
    return context;
  };

interface ThemeProviderProps {
  theme: ThemeConfig;
  children: React.ReactNode;
}

/**
 * Map database theme fields to theme-vars keys.
 *
 * `buttonStyle` is a stored preset, not a CSS var, so it isn't part of
 * ThemeConfig — it expands into three vars here.
 */
const mapThemeToVars = (theme: ThemeConfig & { buttonStyle?: string | null }) => ({
  colorAccent: theme.colorAccent,
  colorAccentForeground: theme.colorAccentForeground,
  // Colors
  colorBackground: theme.colorBackground,
  colorBorder: theme.colorBorder,
  colorCard: theme.colorCard,
  colorCardForeground: theme.colorCardForeground,
  colorDestructive: theme.colorDestructive,
  colorDestructiveForeground: theme.colorDestructiveForeground,
  colorForeground: theme.colorForeground,
  colorInput: theme.colorInput,
  colorMuted: theme.colorMuted,
  colorMutedForeground: theme.colorMutedForeground,
  colorPopover: theme.colorPopover,
  colorPopoverForeground: theme.colorPopoverForeground,
  colorPrimary: theme.colorPrimary,
  colorPrimaryForeground: theme.colorPrimaryForeground,
  colorRing: theme.colorRing,
  colorSecondary: theme.colorSecondary,
  colorSecondaryForeground: theme.colorSecondaryForeground,

  fontFamilyBody: theme.fontFamilyBody,
  // Fonts
  fontFamilyHeading: theme.fontFamilyHeading,
  fontWeightHeading: theme.fontWeightHeading,
  ...buttonStyleVars(theme.buttonStyle),
  fontWeightBody: theme.fontWeightBody,

  // Layout
  radius: theme.radius,
});

const ThemeProvider: React.FC<ThemeProviderProps> = ({ theme, children }) => {
  useEffect(() => {
    // Skip if running on server
    if (typeof window === "undefined") {
      return;
    }

    // Skip if critical CSS already set the theme (SSG with pre-rendered styles)
    const criticalCss = document.getElementById("critical-css");
    if (criticalCss) {
      return;
    }

    // No theme means the build-time/fallback theme already applies
    if (!theme) {
      return;
    }

    // Map database theme fields to theme-vars keys and apply them
    setThemeVars(mapThemeToVars(theme));
  }, [theme]);

  return <ThemeContext.Provider value={{ theme }}>{children}</ThemeContext.Provider>;
};

export { useTheme, ThemeProvider };
